import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote, Star } from "lucide-react";
import { TESTIMONIALS } from "@/data/content";
import { Reveal } from "@/components/site/Reveal";

export const Testimonials = () => {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = TESTIMONIALS.length;
  const go = (d) => setI((x) => (x + d + n) % n);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setI((x) => (x + 1) % n), 7000);
    return () => clearInterval(t);
  }, [paused, n]);

  const t = TESTIMONIALS[i];
  return (
    <section data-testid="testimonials-section" className="bg-white py-24 sm:py-32" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-4">
          <p className="eyebrow text-[#1777AF]">Client voices</p>
          <h2 className="mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl">Trusted to keep things running.</h2>
          <div className="mt-10 flex items-center gap-3">
            <button data-testid="testimonial-prev" aria-label="Previous testimonial" onClick={() => go(-1)} className="grid h-12 w-12 place-items-center rounded-full border border-slate-300 text-slate-900 transition-colors duration-300 hover:border-[#1777AF] hover:bg-[#1777AF] hover:text-white">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button data-testid="testimonial-next" aria-label="Next testimonial" onClick={() => go(1)} className="grid h-12 w-12 place-items-center rounded-full border border-slate-300 text-slate-900 transition-colors duration-300 hover:border-[#1777AF] hover:bg-[#1777AF] hover:text-white">
              <ArrowRight className="h-5 w-5" />
            </button>
            <span className="ml-3 font-mono text-sm text-slate-500" data-testid="testimonial-index">0{i + 1} / 0{n}</span>
          </div>
        </Reveal>
        <div className="relative lg:col-span-8">
          <Quote className="absolute -top-4 left-0 h-16 w-16 text-[#29C1D3]/30" />
          <div className="grid">
            <AnimatePresence initial={false}>
              <motion.figure
                key={i}
                data-testid="testimonial-active"
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="[grid-area:1/1] pt-10"
              >
                <div className="flex gap-1 text-[#1777AF]">{Array.from({ length: 5 }).map((_, k) => <Star key={k} className="h-4 w-4 fill-current" />)}</div>
                <blockquote className="mt-6 font-display text-2xl font-medium leading-snug tracking-tight text-slate-900 sm:text-3xl lg:text-4xl">“{t.quote}”</blockquote>
                <figcaption className="mt-10 flex items-center gap-4">
                  <span className="grid h-12 w-12 place-items-center rounded-full bg-gradient-to-br from-[#1777AF] to-[#29C1D3] font-display font-bold text-white">{t.initials}</span>
                  <span>
                    <span className="block font-semibold text-slate-900">{t.name}</span>
                    <span className="block text-sm text-slate-500">{t.role}</span>
                  </span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>
          <div className="mt-10 flex gap-2">
            {TESTIMONIALS.map((_, k) => (
              <button key={k} aria-label={`Show testimonial ${k + 1}`} data-testid={`testimonial-dot-${k}`} onClick={() => setI(k)} className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-200">
                <span className={`block h-full bg-gradient-to-r from-[#1777AF] to-[#29C1D3] transition-[width] duration-500 ${k === i ? "w-full" : "w-0"}`} />
              </button>
            ))}
          </div>
          <p className="mt-4 text-xs text-slate-500">Placeholder testimonials — replace with real client quotes.</p>
        </div>
      </div>
    </section>
  );
};
